import React, { useState, useEffect } from 'react'
import { EnumType } from 'json-to-graphql-query'
import { createStructuredSelector } from 'reselect'
import { connect } from 'react-redux'
import classNames from 'classnames'

import SlidePanel from 'components/SlidePanel'
import ActionIcon from 'components/ActionIcon'
import AskVisoPrompt from './components/layout/AskVisoPrompt'
import AskVisoHistory from './components/content/AskVisoHistory'
import AskVisoSettings from './components/content/AskVisoSettings'
import AskVisoChat from './components/content/AskVisoChat'
import AskVisoInsertFlows from './components/layout/AskVisoInsertFlows'
import AskVisoAppBuildingSettings from './components/layout/AskVisoAppBuildingSettings'
import { makeSelectFetchWorkspace } from 'containers/Settings/Workspace/selectors'
import { MagicIcon } from 'images/common'
import { handleApiResponse, handleMutation } from '../../utils/request'
import { removeUnusedMetrics } from '../../containers/CustomDashboard/Dashboard/details/charts/utils'
import { getAiConversations, getAiMessages, askAgent, getAgentSettings, createAgentSettings, getAiSuggestions, getModels } from '../../api/ai-agents'
import { getUserLocations } from '../../api/location'
import { getAllDevices } from '../../api/devices'
import { getMetrics } from '../../api/dashboard'
import { TIME_WINDOW_OPTIONS, modeList, modeListAppBuilding } from './constants'
import { defaultNotifier } from 'functions/notificationHandler'
import { AskVisoDisclaimer } from './utils'
import './style.scss'

const AskViso = ({
  color = 'accent0', contextType, legacyPage, isVuiPage, dashboardDetails, showPanel, onClose, askMode, askLabel, askParams, reloadPageData, workspace, hideButton = false, contextChatId, onInsertFlow, existingFlow, closeParentPanel, externalAskMessage, modeParam, availableNodes, openByDefault
}) => {
  const [showViso, toggleShowViso] = useState(openByDefault)
  const [activeTab, setActiveTab] = useState('welcome')
  const [activeContent, setActiveContent] = useState('welcome')
  const [messages, setMessages] = useState([])
  const [outputType, setOutputType] = useState([])
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [promptValue, setPromptValue] = useState('')
  const [modelType, setModelType] = useState({ label: 'In-Depth', value: 'gpt54', badge: 'GPT-5.4' })
  const [chatId, setChatId] = useState(null)
  const [isLoading, toggleLoading] = useState(false)
  const [historyData, setHistoryData] = useState([])
  const [locationsData, setLocationsData] = useState([])
  const [metricsData, setMetricsData] = useState([])
  const [devicesData, setDevicesData] = useState([])
  const [suggestionsList, setSuggestionsList] = useState([])
  const [mode, setMode] = useState([])
  const [modelList, setModelList] = useState([])
  const [showForm, setShowForm] = useState(true)
  const [reloadDataOnClose, toggleReloadDataOnClose] = useState(false)
  const [contentBuildingInput, setContentBuildingInput] = useState({})
  const [settingsData, setSettingsData] = useState({
    id: null,
    dataContext: {
      timeWindow: { type: new EnumType('relative'), value: new EnumType('last_30d') },
      devices: []
    },
    responseStyle: {
      detailLevel: 'headlines only',
      tone: 'executive',
      formatting: {}
    },
    locations: []
  })
  const defaultSort = { field: 'createdAt', type: new EnumType('ASC') }
  const contextTypeEnum = new EnumType(contextType)
  const outputTypeEnum = new EnumType(outputType.length > 0 ? outputType[0].value : 'text')
  const modelTypeEnum = new EnumType(modelType.value)
  const [showAppBuilderForm, setShowAppBuilderForm] = useState(false)
  const lastExternalMsgRef = React.useRef(null)

  const isCreateDemo = contextType === 'demoAppBuilding'
  const isChatActive = activeContent === 'chat' || activeContent === 'createCharts' || activeContent === 'demoAppBuilding'

  useEffect(() => {
    if (showPanel) {
      onAskViso()
    }
  }, [showPanel])


  useEffect(() => {
    if (showPanel) {
      onAskViso()
      if (contextChatId) {
        getMessages(contextChatId)
        setShowForm(false)
      }
    }
  }, [showPanel, contextChatId])

  useEffect(() => {
    if (!externalAskMessage) return
    if (externalAskMessage === lastExternalMsgRef.current) return
    lastExternalMsgRef.current = externalAskMessage

    setActiveContent('chat')
    onAskAgent(externalAskMessage)
  }, [externalAskMessage])

  const postFlowChanges = (flowChanges) => {
    try {
      const hasChanges = Array.isArray(flowChanges) && flowChanges.length > 0
      if (typeof window !== 'undefined' && window.parent && hasChanges) {
        window.parent.postMessage({ type: 'viso:flowChanges', flowChanges }, '*')
      }
    } catch (_) { }
  }

  useEffect(() => {
    const selectedOutputType = mode && mode[0] && mode[0].value

    if (contextType === 'appBuilding' && selectedOutputType === 'rebuild') {
      setShowAppBuilderForm(selectedOutputType === 'rebuild')
      setActiveContent('insertFlow')
    } else {
      setShowAppBuilderForm(false);
    }
  }, [contextType, mode])

  // Keep UI in sync with latest existingFlow from parent (AppBuilderAgentAi)
  useEffect(() => {
    if (contextType !== 'appBuilding') return

    // Wait for existingFlow to be resolved by parent. If undefined, still loading.
    if (typeof existingFlow === 'undefined') {
      toggleLoading(true)
      return
    }

    if (mode[0]?.value) {
      const modeType = {
        'explain': 'Explain app',
        'optimize': 'Optimize app',
        'expand': 'Expand app',
        'rebuild': 'Create app'
      }

      const desired = { label: modeType[mode[0].value], value: mode[0].value }
      const same =
        mode.length === 1 &&
        mode[0].value === desired.value &&
        mode[0].label === desired.label

      if (!same) {
        onModeChange([desired])
      }
      setActiveContent(mode[0].value === 'rebuild' ? 'insertFlow' : 'welcome')

      return toggleLoading(false)
    }


    // If existingFlow is explicitly empty/null, show Insert Flow (Create app)
    {
      const desired = { label: 'Create app', value: 'rebuild' }
      const same = mode.length === 1 && mode[0].value === desired.value && mode[0].label === desired.label
      // if (!same) {
      //   setMode([desired])
      // }
    }
    // setActiveContent('insertFlow')
    toggleLoading(false)
  }, [existingFlow, contextType, mode, modeParam])


  const onFetchModels = (response) => {
    setModelList(response.map(item => ({ label: item.type, value: item.name, badge: item.name })))
  }

  useEffect(() => {
    const fetchModels = async () => {
      const modelList = await getModels();
      handleApiResponse(modelList, onFetchModels, 'models');
    };
    fetchModels()
  }, [])

  const getMessages = async (conversationId, updateOnly = false) => {
    !updateOnly && setMessages([])
    setOutputType([])
    const onSuccess = (response) => {
      const data = response.data
      if (updateOnly) {
        const last = Array.isArray(data) ? data.at(-1) : undefined
        last && setMessages(prev => [...prev, last])
        const lastFlow = last && last.content && last.content.flowChanges
        postFlowChanges(lastFlow)
      } else {
        setChatId(conversationId)
        setMessages(data)
        const last = Array.isArray(data) ? data.at(-1) : undefined
        const lastFlow = last && last.content && last.content.flowChanges
        postFlowChanges(lastFlow)

      }
    }
    toggleLoading(true)
    try {
      const messageData = await getAiMessages({ conversationId, sort: defaultSort })
      handleApiResponse(messageData, onSuccess, 'messages')
    } catch (error) {
      defaultNotifier(error)
    } finally {
      toggleLoading(false)
    }
  }

  const onHistoryClick = async () => {
    setActiveContent('history')
    toggleLoading(true)
    setActiveTab('history')
    setOutputType([])
    try {
      const conversationData = await getAiConversations({ contextType: contextTypeEnum, sort: defaultSort, limit: 1000 })
      handleApiResponse(conversationData, (response) => setHistoryData(response.data), 'conversations')
    } catch (error) {
      defaultNotifier(error)
    } finally {
      toggleLoading(false)
    }
  }

  const onSettingsClick = () => {
    if (contextType === 'appBuilding') {
      setActiveTab('appBuildingSettings')
      return setActiveContent('appBuildingSettings')
    }

    setActiveTab('settings')
    setActiveContent('settings')
    setOutputType([])
  }

  const onAskAgent = async (userMessage, conversation) => {
    const selectedOutputType = mode && mode[0] && mode[0].value

    if (userMessage) {
      // In app building 'Create app' mode, do not hit backend; show local UI
      const isCreateApp = contextType === 'appBuilding' && selectedOutputType === 'rebuild'

      if (isCreateApp) {
        const newMessage = [{
          senderRole: 'model',
          content: {
            reasoning: null,
            suggestion: [],
            text: '',
            response: [
              { type: 'markdown', content: userMessage, table: null, chart: null, links: Array(0) },
            ]
          },
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString()
        }]

        const addUserMessage = [...messages, ...newMessage]
        setMessages(addUserMessage);

        return
      }

      const safeOutputTypeEnum = contextType === 'appBuilding' ? new EnumType('text') : outputTypeEnum

      const payload = {
        contextType: contextTypeEnum,
        model: modelTypeEnum,
        message: `${userMessage}${outputType.length > 0 ? ` Please output this as a ${outputType[0].value}` : ''}`,
        outputType: outputTypeEnum,
        ...(conversation && { conversationId: conversation }),
        ...(askParams && { ...askParams }),
        ...(mode.length > 0 && { mode: new EnumType(mode[0].value) }),
        ...(mode?.[0]?.value === 'autoBuildCharts' ? { dashboardBuildingInput: contentBuildingInput } : {}),
        ...(isCreateDemo && !!contentBuildingInput && Object.keys(contentBuildingInput).length > 0 && { demoAppInput: contentBuildingInput }),
      }

      if (contextType === 'appBuilding') {
        payload = {
          contextType: contextTypeEnum,
          outputType: safeOutputTypeEnum,
          model: modelTypeEnum,
          message: `${userMessage}${(contextType !== 'appBuilding' && outputType.length > 0) ? ` Please output this as a ${outputType[0].value}` : ''}`,
          ...(conversation && { conversationId: conversation }),
          appBuildingInput: {
            availableNodes: JSON.stringify(availableNodes),
            existingFlow: JSON.stringify(existingFlow),
            mode: new EnumType(selectedOutputType || 'explain')
          }
        }
      }

      const newMessage = [{
        senderRole: 'user',
        content: {
          text: userMessage,
        },
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      }]
      const addUserMessage = [...messages, ...newMessage]
      setMessages(addUserMessage)

      const onSuccess = (response) => {
        const { conversationId } = response.data.askAgent

        if (contextType === 'appBuilding') {
          const immediateFlow = response.data.askAgent?.message?.flowChanges
          postFlowChanges(immediateFlow)
        }

        setChatId(conversationId)
        getMessages(conversationId, true)
        setShowForm(false)
      }
      toggleLoading(true)

      try {
        const response = await askAgent(payload)
        handleMutation(response, onSuccess)
      } catch (error) {
        defaultNotifier(error)
      } finally {
        setOutputType([])
        toggleLoading(false)
      }
    }
  }

  const onClosePanel = () => {
    if (contextType === 'appBuilding') {
      closeParentPanel()
      setMode([])
      onClose && onClose()
      setActiveContent('welcome')
      setActiveTab('welcome')
      setMessages([])
      setChatId(null)
      toggleLoading(false)
      setHistoryData([])
      return setOutputType([])
    }

    toggleShowViso(false)
    setMode([])
    setContentBuildingInput({})
    setActiveContent('welcome')
    setActiveTab('welcome')
    setMessages([])
    setChatId(null)
    toggleLoading(false)
    setHistoryData([])
    setOutputType([])
    setSuggestionsList([])
    setPromptValue('')
    setIsFullscreen(false)
    setShowForm(true)
    onClose && onClose()
    reloadDataOnClose && reloadPageData()
  }

  const onModeChange = (mode) => {
    setMode(mode)
    getSuggestionList(mode.length > 0 && mode[0].value)
  }

  const getSuggestionList = async (modeValue) => {
    if (modeValue.length === 0) return
    const defaultMode = contextType === 'appBuilding' ? modeListAppBuilding.find(item => item.value === modeValue) || modeListAppBuilding[0].value : modeList[0].value
    const modeEnum = new EnumType(modeValue || defaultMode)
    if (modeValue === 'autoBuildCharts') return

    const setSuggestions = (response) => {
      if (response && response.length > 0) {
        activeContent === 'welcome' && setPromptValue(response[0])
        setSuggestionsList(response)
      } else {
        activeContent === 'welcome' && setPromptValue(askMode?.label || '')
      }
    }
    const response = await getAiSuggestions({ contextType: contextTypeEnum, mode: modeEnum })
    handleApiResponse(response, setSuggestions, 'suggestions')
  }

  const setAskVisoContent = (mode, content) => {
    if (mode === 'recommendCharts' || mode === 'autoBuildCharts' || mode === 'reviewCharts' || mode === 'improveChart') {
      setActiveContent('createCharts')
    } else if (content) {
      setActiveContent(content)
    }
  }

  const onAskViso = async (modeValue) => {
    const getModeValue = askMode?.value || modeValue
    const getContent = isCreateDemo ? 'demoAppBuilding' : undefined
    toggleShowViso(true)
    toggleLoading(true)

    if (contextType === 'appBuilding') {
      // If existingFlow is not yet loaded, keep loading and let the effect above decide once available
      if (typeof existingFlow === 'undefined') {
        return
      }

      if (existingFlow?.nodes?.length > 0) {
        setOutputType([{ label: 'Optimize app', value: 'optimize' }])
        setActiveContent('welcome')
        toggleLoading(false)
        return
      }

      setActiveContent(mode?.[0]?.value === 'rebuild' ? 'insertFlow' : 'welcome')
      toggleLoading(false)
    }


    setAskVisoContent(getModeValue, getContent)

    if (contextType === 'dashboardBuilding') {
      setModelType(modelList[0])
    }
    const onSuccess = (response) => {
      response.data.createAgentSettings && setSettingsData(response.data.createAgentSettings)
    }

    const setLocations = (response) => {
      const mapOptions = response.map(location => ({
        value: location._id,
        label: location.name
      }))
      setLocationsData(mapOptions)
    }

    const setDevices = (response) => {
      const mapOptions = response.data.map(device => ({
        value: device.id,
        label: device.name,
        locationId: device.location?.id
      }))
      setDevicesData(mapOptions)
    }

    const setMetrics = (response) => {
      const mapOptions = removeUnusedMetrics(response.data, workspace?.metricsTableEnabled)
      setMetricsData(mapOptions)
    }

    try {
      if (contextType !== 'appBuilding') {
        const locationsResponse = await getUserLocations()
        handleApiResponse(locationsResponse, setLocations, 'userLocations')

        const devicesResponse = await getAllDevices()
        handleApiResponse(devicesResponse, setDevices, 'devices')
      }
      getSuggestionList(getModeValue)
      askMode && setMode([askMode])

      const settingsResponse = await getAgentSettings({ contextType: contextTypeEnum })

      if (settingsResponse.data?.data?.agentSettings) {
        setSettingsData(settingsResponse.data.data.agentSettings)
      } else {
        const createPayload = {
          dataContext: {
            timeWindow: {
              type: new EnumType('relative'),
              value: new EnumType((TIME_WINDOW_OPTIONS.find(opt => opt.label === 'Last 30 days') || TIME_WINDOW_OPTIONS[0]).apiValue)
            },
            devices: settingsData.dataContext.devices
          },
          responseStyle: {
            detailLevel: settingsData.responseStyle.detailLevel,
            tone: new EnumType(settingsData.responseStyle.tone)
          }
        }

        const settingsResponse = await createAgentSettings({ contextType: contextTypeEnum, ...createPayload })
        handleMutation(settingsResponse, onSuccess)
      }

      if (contextType === 'dashboardBuilding') {
        const metricResponse = await getMetrics()
        handleApiResponse(metricResponse, setMetrics, 'metrics')
      }
    } catch (error) {
      defaultNotifier(error)
    } finally {
      toggleLoading(false)
    }
  }

  const onConversationClick = (conversationId, item = {}) => {
    getMessages(conversationId)
    setActiveTab('welcome')
    setActiveContent('chat')

    if (item?.demoAppInput) {
      const payload = Object.fromEntries(
        Object.entries(item.demoAppInput).map(([key, value]) => [key, new EnumType(value)])
      )
      setContentBuildingInput(payload)
    }
  }

  return (
    <div className={classNames('ask-viso', legacyPage && 'ask-viso--legacy', isVuiPage && 'ask-viso--vui')}>
      {!hideButton && <ActionIcon
        id='askViso'
        onClick={() => onAskViso()}
        icon={<MagicIcon />}
        text={askLabel || 'Ask Viso'}
        color={color}
      />
      }
      <SlidePanel
        isVisible={showViso}
        noScroll={activeTab === 'settings'}
        hasFadeOutScroll={activeContent === 'history' || isChatActive}
        color={color}
        onClose={onClosePanel}
        onFullscreen={setIsFullscreen}
        showFullscreen
        headerProps={{
          title: 'Ask Viso',
          icon: <MagicIcon />,
          tabs: [
            {
              label: isCreateDemo ? 'New Chat' : 'Viso Assistant',
              onClick: () => {
                setMessages([])
                setChatId(null)
                setActiveTab('welcome')
                setAskVisoContent(mode[0]?.value || '', isCreateDemo ? 'demoAppBuilding' : 'welcome')
                setPromptValue(suggestionsList && suggestionsList.length > 0 ? suggestionsList[0] : '')
                isCreateDemo && setShowForm(true)
                if(contextType === 'appBuilding' && (mode?.length > 0 && mode[0].value === 'rebuild')) {
                  setActiveContent('insertFlow')
                }
              },
              active: activeTab === 'welcome'
            },
            {
              label: 'History',
              onClick: () => {
                onHistoryClick()
                setPromptValue('')
              },
              active: activeTab === 'history'
            },
            ...(isCreateDemo ? [] : [{
              label: 'Settings',
              onClick: onSettingsClick,
              active: activeTab === 'settings' || activeTab === 'appBuildingSettings'
            }])
          ]
        }}
        customFooter={isChatActive &&
          <>
            <AskVisoPrompt
              onAskAgent={onAskAgent}
              color={color}
              chatId={chatId}
              contextType={contextType}
              isLoading={isLoading}
              setMode={onModeChange}
              mode={mode}
              setPromptValue={setPromptValue}
              suggestionsList={suggestionsList}
              promptValue={promptValue}
              setModelType={setModelType}
              modelType={modelType}
              modelList={modelList}
              isFullscreen={isFullscreen}
              isFooter
              hideModes={isCreateDemo}
            />
            <AskVisoDisclaimer />
          </>
        }
      >
        <>
          {activeContent === 'welcome' &&
            <AskVisoPrompt
              contextType={contextType}
              onAskAgent={(prompt) => {
                onAskAgent(prompt)
                setActiveContent('chat')
              }}
              color={color}
              setModelType={setModelType}
              modelType={modelType}
              isFullscreen={isFullscreen}
              isLoading={isLoading}
              setMode={onModeChange}
              mode={mode}
              setPromptValue={setPromptValue}
              promptValue={promptValue}
              suggestionsList={suggestionsList}
              isWelcome
              modelList={modelList}
              hideModes={isCreateDemo}
            />
          }
          {isChatActive &&
            <AskVisoChat
              messages={messages}
              workspace={workspace}
              color={color}
              isLoading={isLoading}
              activeContent={activeContent}
              toggleLoading={toggleLoading}
              getMessages={() => getMessages(chatId)}
              chatId={chatId}
              setMessages={setMessages}
              setPromptValue={setPromptValue}
              isFullscreen={isFullscreen}
              devicesData={devicesData}
              toggleReloadDataOnClose={toggleReloadDataOnClose}
              dashboardDetails={dashboardDetails}
              mode={mode}
              metricsData={metricsData}
              setContentBuildingInput={setContentBuildingInput}
              showForm={showForm}
              onAskAgent={onAskAgent}
            />
          }
          {activeContent === 'history' &&
            <AskVisoHistory
              data={historyData}
              color={color}
              onConversationClick={onConversationClick}
              isLoading={isLoading}
              getHistory={onHistoryClick}
              isFullscreen={isFullscreen}
            />
          }
          {activeContent === 'settings' &&
            <AskVisoSettings
              color={color}
              locationsData={locationsData}
              devicesData={devicesData}
              settingsData={settingsData}
              setSettingsData={setSettingsData}
              setActiveTab={setActiveTab}
              isFullscreen={isFullscreen}
              workspace={workspace}
            />
          }
          {activeContent === 'appBuildingSettings' &&
              <AskVisoAppBuildingSettings
                color={color}
                locationsData={locationsData}
                devicesData={devicesData}
                settingsData={settingsData}
                setSettingsData={setSettingsData}
                setActiveTab={setActiveTab}
                isFullscreen={isFullscreen}
              />
            }
            {activeContent === 'insertFlow' &&
              <AskVisoInsertFlows
                metricsData={metricsData}
                contextType={contextType}
                onAskAgent={(prompt) => {
                  onAskAgent(prompt)
                  // setActiveContent('chat')
                }}
                chatId={chatId}
                mode={mode}
                setMode={onModeChange}
                onModeChange={onModeChange}
                messages={messages}
                setActiveContent={setActiveContent}
                color={'accent3'}
                onInsertFlow={onInsertFlow}
                setModelType={setModelType}
                modelType={modelType}
                isFullscreen={isFullscreen}
                isLoading={isLoading}
                setOutputType={setOutputType}
                outputType={outputType}
                setPromptValue={setPromptValue}
                promptValue={promptValue}
                isNewFlow
              />
            }

          {!isChatActive && <AskVisoDisclaimer />}
        </>
      </SlidePanel>
    </div>
  )
}

const mapStateToProps = createStructuredSelector({ workspace: makeSelectFetchWorkspace() })
const withConnect = connect(mapStateToProps, null)
export default withConnect(AskViso)